import AutocompleteCombobox from '../../combobox/autocomplete-combobox';
import DropdownCombobox from '../../combobox/dropdown-combobox';
import ProductSelectorEmbed from './product-selector';

declare global {
  interface Window {
    nhtProductSelectorTireData: TireData;
  }
}

export type TireData = {
  industries: any;
  items: any;
};

export default class ProductSelectorEmbedTire {
  dom: {
    form: HTMLFormElement;
    industry: HTMLSelectElement;
    equipment: HTMLSelectElement;
    tireSize: HTMLSelectElement;
    rim: HTMLSelectElement;
  };

  #data!: TireData;

  state = {
    industry: '',
    equipment: '',
    tireSize: '',
    rim: '',
  };

  #updatingFilters = false;

  initialDisabledState = {};

  parent: ProductSelectorEmbed;

  /**
   * Embedded tire selector logic.
   *
   * @param {HTMLElement} tabElement - Tab element containing the tire selector
   * @param {ProductSelectorEmbed} parent - Parent product selector instance
   */
  constructor(tabElement: HTMLElement, parent: ProductSelectorEmbed) {
    this.dom = {} as any;
    this.parent = parent;

    if(window.nhtProductSelectorTireData) {
      this.#data = window.nhtProductSelectorTireData;
    }

    const formEl = tabElement.querySelector('form');

    if(formEl) {
      this.dom.form = formEl;

      Object.keys(this.state).forEach(key => {
        this.dom[key] = formEl.querySelector(`[name="${key}"]`);
      });

      // Store initial disabled fields state
      this.storeInitialDisabledState();
    }

    if(this.#data && Object.keys(this.dom).every(key => this.dom[key])) {
      // Data and necessary dom elements found
      console.log('[ProductSelectorEmbedTire]: init tire module');

      this.#initState();
      this.#initDropdowns();
      this.#attachEventListeners();
    } else {
      console.error('[ProductSelectorEmbedTire]: missing data or dom elements');
    }
  }

  /**
   * Initialises state from dom select values
   */
  #initState() {
    const initialState = {};
    Object.keys(this.state).forEach(key => {
      if(this.dom[key]) {
        initialState[key] = this.dom[key].value;
      }
    });
    this.state = {...this.state, ...initialState};
    this.#updateSearchFilters(this.state);
  }


  /**
   * Initialises custom dropdowns for native selects
   */
  #initDropdowns() {
    Object.keys(this.dom)
      .filter(key => key !== 'form')
      .forEach(key => {
        const selectEl = this.dom[key];

        if(key === 'tireSize') {
          new AutocompleteCombobox({
            select: selectEl,
            cssClassPrefix: 'combobox',
            browserAutocomplete: 'off'
          });
        } else {
          new DropdownCombobox({
            select: selectEl,
            cssClassPrefix: 'combobox',
          });
        }
      });
  }

  /**
   * Attach event listeners to relevant dom elements
   */
  #attachEventListeners() {
    Object.keys(this.dom).forEach(key => {
      if(key === 'form') {
        this.dom[key].addEventListener('submit', this.onFormSubmit.bind(this));
        this.dom[key].addEventListener('reset', this.onFormReset.bind(this));
      } else {
        this.dom[key].addEventListener('change', this.onSelectChange.bind(this));
      }
    });
  }

  /**
   * Stores the initial disabled state of optional fields and submit button.
   */
  storeInitialDisabledState() {
    Object.keys(this.dom).forEach((key) => {
      if (key !== 'form' && this.dom[key]) {
        this.initialDisabledState[key] = this.dom[key].disabled;
      }
    });

    // Store the disabled state of the submit button
    const submitButton = this.dom.form.querySelector('[type="submit"]') as HTMLButtonElement;
    if (submitButton) {
      this.initialDisabledState['submitButton'] = submitButton.disabled;
    }
  }

  /**
   * Enables optional form elements within the form. This method iterates over
   * the dom property of the object, enabling all form elements except for the main form.
   * Also enables the submit button if it exists.
   */
  enableOptionalFormElements() {
    Object.keys(this.dom).forEach(key => {
      if (key !== 'form' && this.dom[key]) {
        this.dom[key].disabled = false;
      }
    });

    // Enable the submit button
    const submitButton = this.dom.form.querySelector('[type="submit"]') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = false;
    }
  }

  /**
   * Reapply the initial disabled state of optional fields and submit button.
   */
  disableOptionalFormElements() {
    Object.keys(this.dom).forEach((key) => {
      if (key !== 'form' && key !== 'industry' && this.dom[key]) {
        this.dom[key].disabled = this.initialDisabledState[key];
      }
    });

    // Disable the submit button based on initial state
    const submitButton = this.dom.form.querySelector('[type="submit"]') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = this.initialDisabledState['submitButton'];
    }
  }

  /**
   * Handles select change events
   *
   * @param {Event} event - Change event
   */
  onSelectChange(event) {
    // Callback was caused by internal change in filters
    if (this.#updatingFilters) {
      return;
    }

    // Update the state with the new value
    this.update({[event.target.name]: event.target.value});

    // Enable or disable optional form elements, based on the selection of 'industry'
    if (this.state.industry && this.state.industry.trim() !== '') {
      this.enableOptionalFormElements();
    } else {
      this.disableOptionalFormElements();
    }
  }

  /**
   * Handles form submit events
   *
   * @param {Event} event - Submit event
   */
  onFormSubmit(event) {
    event.preventDefault();

    this.parent.setLoading(true);

    // Get industry page url
    let targetLocation;
    const page = this.#data.industries.find(industry => industry.id === this.state.industry)?.page;
    if(page) {
      targetLocation = new URL(page, document.location.href);
      targetLocation.searchParams.set('equipment', this.state.equipment);
      targetLocation.searchParams.set('size', this.state.tireSize);
      targetLocation.searchParams.set('rim', this.state.rim);

      if(event.target.scrollto?.value) {
        targetLocation.hash = event.target.scrollto.value;
      }
    }

    if(targetLocation.href) {
      this.#sendEvent('product_selector_submit', {
        product_type: 'Tire',
        product_industry: this.state.industry,
        product_machine: this.state.equipment,
      });

      document.location.href = targetLocation.href;
    } else {
      this.parent.setLoading(false);
    }
  }

  /**
   * Handles form reset events
   *
   * @param {Event} event - Reset event
   */
  onFormReset(event) {
    const values = { ...this.state };

    for (let key in values) {
      const defaultOption = this.dom[key].querySelector('[data-default]') || this.dom[key].options[0];
      values[key] = defaultOption?.value || '';
    }

    this.update(values);

    // Disable optional fields
    if (!values.industry || values.industry.trim() === '') {
      this.disableOptionalFormElements();
    }
  }

  /**
   * Filter products based on given values. All values must match.
   *
   * @param {TireSelectorValues} values - Values to filter with.
   * @returns {object[]} Array of products that match given values.
   */
  filterProducts(values) {
    const matchingItems = this.#data.items.filter(item => {
      return Object.keys(values).every(key => {
        let stateValue = values[key];
        let itemValue = item[key];

        if(stateValue && String(itemValue) !== stateValue) {
          return false;
        }

        return true;
      });
    });

    return matchingItems;
  }

  /**
   * Updates input values and available options to match given values.
   *
   * @param {TireSelectorValues} values - Values to set
   */
  #updateSearchFilters(values) {
    const changed: HTMLSelectElement[] = [];

    Object.keys(values).forEach((field) => {
      const selectElement = this.dom[field] as HTMLSelectElement;

      if(selectElement) {
        if(selectElement.value !== values[field]) {
          changed.push(selectElement);
        }

        const availableOptions = this.filterProducts({...values, [field]: ''}).map(item => item[field]);

        for (const opt of selectElement.options) {
          opt.hidden = opt.value && !availableOptions.includes(opt.value) ? true : false;
          opt.selected = !opt.hidden && values[field] === opt.value;
        }
      }
    });

    // Trigger native event on all elements that were changed
    // Combobox needs this, if it's used
    this.#updatingFilters = true;
    changed.forEach((el) => {
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.#updatingFilters = false;
  }

  /**
   * Filters products and updates active search filters based on given values.
   *
   * @param {TireSelectorValues} newValues Values to set.
   */
  update(newValues = {}) {
    const oldValues = {...this.state};
    const values = { ...oldValues, ...newValues };
    this.state = values;

    this.#updateSearchFilters(this.state);
    this.#triggerEvents(this.state, oldValues);
  }

  /**
   * Triggers events based on changed values.
   *
   * @param {TireSelectorValues} values Current values.
   * @param {TireSelectorValues} oldValues Old values.
   */
  #triggerEvents(values, oldValues) {
    if (values.industry !== oldValues.industry) {
      this.#sendEvent('product_selector_embed_interaction', {
        product_type: 'Tire',
        product_option: 'Industry',
        product_option_value: values.industry,
      });
    }
    if (values.equipment !== oldValues.equipment) {
      this.#sendEvent('product_selector_embed_interaction', {
        product_type: 'Tire',
        product_option: 'Equipment',
        product_option_value: values.equipment,
      });
    }
    if (values.tireSize !== oldValues.tireSize) {
      this.#sendEvent('product_selector_embed_interaction', {
        product_type: 'Tire',
        product_option: 'Tire size',
        product_option_value: values.tireSize,
      });
    }
    if (values.rim !== oldValues.rim) {
      this.#sendEvent('product_selector_embed_interaction', {
        product_type: 'Tire',
        product_option: 'Rim',
        product_option_value: values.rim,
      });
    }
  }

  /**
   * Send tire selector analytics event to datalayer.
   *
   * @param {string} name - Event name
   * @param {object} [data] - Optional event data
   */
  #sendEvent(name, data = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: name,
      ...data
    });
  }


}

/**
 * @typedef {object} TireSelectorValues
 * @property {string} industry - Industry (usage group) id
 * @property {string} equipment - Equipment id
 * @property {string} tireSize - Full tire size string (eg. 600/65R34)
 * @property {string} rim - Rim diameter
 * @private
 */
